import { PlusOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App, Button, Card, Checkbox, DatePicker, Flex, Form, Input, InputNumber, Modal, Select, Space, Switch, Table, Tag, Typography } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { useDeferredValue, useState } from "react";
import { api, unwrap, type Schemas } from "@/api/client";
import { useApplyFieldErrors, useErrorText } from "@/api/errors";
import { useAuth } from "@/auth/AuthProvider";
import { useI18n } from "@/i18n";
import { CLIENT_PAGING } from "@/components/list/useListPage";
import { PLATFORMS, useConnections } from "../channels/hooks";

type Rule = Schemas["HoldRule"];
type Conditions = Schemas["HoldConditions"];
type Values = {
  name: string;
  note?: string;
  active: boolean;
  untilAt?: Dayjs | null;
  all?: boolean;
  connectionIds?: string[];
  platforms?: Conditions["platforms"];
  paymentMethods?: string[];
  minTotal?: number | null;
  maxTotal?: number | null;
  skuIds?: string[];
  minUnits?: number | null;
  carriers?: string[];
  orderedBefore?: Dayjs | null;
};

/**
 * Order > Aturan tahan (Plan §6.11): orders matching a rule wait in Baru, with
 * their stock reserved, until someone releases them or the rule ends.
 */
export function HoldRules() {
  const { t, formatDateTime, formatMoney } = useI18n();
  const { me, can } = useAuth();
  const { message, modal } = App.useApp();
  const errorText = useErrorText();
  const queryClient = useQueryClient();
  const tz = me?.workspace?.timezone;
  const manage = can("orders.manage");
  const [editing, setEditing] = useState<Rule | "new" | null>(null);
  const rules = useQuery({ queryKey: ["hold-rules"], queryFn: () => unwrap(api.GET("/v1/order-hold-rules")) });
  const connections = useConnections();
  const storeName = (id: string) => connections.data?.items.find((c) => c.id === id)?.name ?? id;
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["hold-rules"] });
    void queryClient.invalidateQueries({ queryKey: ["orders"] });
  };
  const toggle = useMutation({
    mutationFn: (r: Rule) =>
      unwrap(
        api.PATCH("/v1/order-hold-rules/{ruleID}", {
          params: { path: { ruleID: r.id } },
          body: { name: r.name, note: r.note, active: !r.active, conditions: r.conditions, untilAt: r.untilAt },
        }),
      ),
    onSuccess: refresh,
    onError: (err) => void message.error(errorText(err)),
  });
  const remove = useMutation({
    mutationFn: (r: Rule) => unwrap(api.DELETE("/v1/order-hold-rules/{ruleID}", { params: { path: { ruleID: r.id } } })),
    onSuccess: refresh,
    onError: (err) => void message.error(errorText(err)),
  });

  const summary = (c: Conditions) => {
    const parts: string[] = [];
    if (c.all) parts.push(t("orders.rule.allOrders"));
    if (c.connectionIds?.length) parts.push(`${t("orders.rule.stores")}: ${c.connectionIds.map(storeName).join(", ")}`);
    if (c.platforms?.length) parts.push(`${t("orders.rule.platforms")}: ${c.platforms.map((p) => t(`platform.${p}`)).join(", ")}`);
    if (c.paymentMethods?.length) parts.push(`${t("orders.rule.payment")}: ${c.paymentMethods.join(", ")}`);
    if (c.minTotal != null) parts.push(`${t("orders.rule.minTotal")} ${formatMoney(c.minTotal)}`);
    if (c.maxTotal != null) parts.push(`${t("orders.rule.maxTotal")} ${formatMoney(c.maxTotal)}`);
    if (c.skuIds?.length) parts.push(t("orders.rule.skuCount", { n: c.skuIds.length }));
    if (c.minUnits != null) parts.push(t("orders.rule.minUnitsValue", { n: c.minUnits }));
    if (c.carriers?.length) parts.push(`${t("orders.rule.carriers")}: ${c.carriers.join(", ")}`);
    if (c.orderedBefore) parts.push(`${t("orders.rule.orderedBefore")} ${formatDateTime(c.orderedBefore, tz)}`);
    return parts.join(" · ");
  };

  return (
    <>
      <Flex justify="space-between" align="center" wrap gap={12} style={{ marginBottom: 8 }}>
        <Typography.Title level={2} style={{ margin: 0, fontSize: 28 }}>
          {t("orders.holdRules")}
        </Typography.Title>
        {manage && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setEditing("new")}>
            {t("orders.rule.add")}
          </Button>
        )}
      </Flex>
      <Typography.Paragraph type="secondary">{t("orders.rule.help")}</Typography.Paragraph>
      <CancelSettings manage={manage} />
      <Table<Rule>
        rowKey="id"
        loading={rules.isPending}
        dataSource={rules.data?.items ?? []}
        pagination={CLIENT_PAGING}
        locale={{ emptyText: t("orders.rule.empty") }}
        columns={[
          {
            title: t("common.name"),
            dataIndex: "name",
            render: (v: string, r) => (
              <Space orientation="vertical" size={0}>
                <Typography.Text strong>{v}</Typography.Text>
                {r.note && (
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {r.note}
                  </Typography.Text>
                )}
              </Space>
            ),
          },
          { title: t("orders.rule.conditions"), render: (_, r) => summary(r.conditions) },
          {
            title: t("orders.rule.until"),
            dataIndex: "untilAt",
            width: 170,
            render: (v: string | null) => (v ? formatDateTime(v, tz) : "—"),
          },
          { title: t("orders.rule.held"), dataIndex: "held", width: 110, align: "right", render: (n: number) => (n > 0 ? <Tag color="gold">{n}</Tag> : 0) },
          {
            title: t("orders.rule.active"),
            width: 90,
            render: (_, r) => <Switch checked={r.active} disabled={!manage} loading={toggle.isPending && toggle.variables?.id === r.id} onChange={() => toggle.mutate(r)} />,
          },
          ...(manage
            ? [
                {
                  title: "",
                  width: 150,
                  render: (_: unknown, r: Rule) => (
                    <Space>
                      <Button size="small" onClick={() => setEditing(r)}>
                        {t("common.edit")}
                      </Button>
                      <Button
                        size="small"
                        danger
                        onClick={() =>
                          modal.confirm({
                            title: t("orders.rule.delete"),
                            content: t("orders.rule.deleteConfirm", { name: r.name, n: r.held }),
                            okButtonProps: { danger: true },
                            okText: t("common.delete"),
                            cancelText: t("common.cancel"),
                            onOk: () => remove.mutateAsync(r),
                          })
                        }
                      >
                        {t("common.delete")}
                      </Button>
                    </Space>
                  ),
                },
              ]
            : []),
        ]}
      />
      <RuleModal rule={editing} onClose={() => setEditing(null)} onSaved={refresh} />
    </>
  );
}

function RuleModal({ rule, onClose, onSaved }: { rule: Rule | "new" | null; onClose: () => void; onSaved: () => void }) {
  const { t } = useI18n();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const applyFieldErrors = useApplyFieldErrors();
  const [form] = Form.useForm<Values>();
  const all = Form.useWatch("all", form);
  const connections = useConnections();
  const [skuSearch, setSkuSearch] = useState("");
  const q = useDeferredValue(skuSearch.trim());
  const skus = useQuery({
    queryKey: ["skus", "pick", "rules", q],
    queryFn: () => unwrap(api.GET("/v1/skus", { params: { query: { status: "active", limit: 50, ...(q ? { q } : {}) } } })),
    enabled: rule !== null,
  });
  const existing = rule && rule !== "new" ? rule : null;
  const c = existing?.conditions ?? {};
  const initial: Values = existing
    ? {
        name: existing.name,
        note: existing.note,
        active: existing.active,
        untilAt: existing.untilAt ? dayjs(existing.untilAt) : null,
        all: c.all,
        connectionIds: c.connectionIds,
        platforms: c.platforms,
        paymentMethods: c.paymentMethods,
        minTotal: c.minTotal,
        maxTotal: c.maxTotal,
        skuIds: c.skuIds,
        minUnits: c.minUnits,
        carriers: c.carriers,
        orderedBefore: c.orderedBefore ? dayjs(c.orderedBefore) : null,
      }
    : { name: "", active: true };
  const save = useMutation({
    mutationFn: (v: Values) => {
      const conditions: Conditions = v.all
        ? { all: true }
        : {
            ...(v.connectionIds?.length ? { connectionIds: v.connectionIds } : {}),
            ...(v.platforms?.length ? { platforms: v.platforms } : {}),
            ...(v.paymentMethods?.length ? { paymentMethods: v.paymentMethods } : {}),
            ...(v.minTotal != null ? { minTotal: v.minTotal } : {}),
            ...(v.maxTotal != null ? { maxTotal: v.maxTotal } : {}),
            ...(v.skuIds?.length ? { skuIds: v.skuIds } : {}),
            ...(v.minUnits != null ? { minUnits: v.minUnits } : {}),
            ...(v.carriers?.length ? { carriers: v.carriers } : {}),
            ...(v.orderedBefore ? { orderedBefore: v.orderedBefore.toISOString() } : {}),
          };
      const body = { name: v.name, note: v.note ?? "", active: v.active, conditions, untilAt: v.untilAt ? v.untilAt.toISOString() : null };
      return existing
        ? unwrap(api.PATCH("/v1/order-hold-rules/{ruleID}", { params: { path: { ruleID: existing.id } }, body }))
        : unwrap(api.POST("/v1/order-hold-rules", { body }));
    },
    onSuccess: () => {
      void message.success(t("orders.rule.saved"));
      onSaved();
      onClose();
    },
    onError: (err) => {
      if (!applyFieldErrors(form, err)) void message.error(errorText(err));
    },
  });
  return (
    <Modal
      open={rule !== null}
      title={existing ? t("orders.rule.edit") : t("orders.rule.add")}
      onCancel={onClose}
      onOk={() => form.submit()}
      okText={t("common.save")}
      cancelText={t("common.cancel")}
      confirmLoading={save.isPending}
      width={640}
      destroyOnHidden
      afterClose={() => form.resetFields()}
    >
      <Form form={form} layout="vertical" initialValues={initial} onFinish={(v) => save.mutate(v)} requiredMark={false}>
        <Form.Item name="name" label={t("common.name")} rules={[{ required: true, whitespace: true, max: 100 }]}>
          <Input />
        </Form.Item>
        <Form.Item name="note" label={t("orders.rule.note")} extra={t("orders.rule.noteHelp")}>
          <Input maxLength={300} />
        </Form.Item>
        <Typography.Text strong>{t("orders.rule.conditions")}</Typography.Text>
        <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
          {t("orders.rule.conditionsHelp")}
        </Typography.Paragraph>
        <Form.Item name="all" valuePropName="checked">
          <Checkbox>{t("orders.rule.allOrders")}</Checkbox>
        </Form.Item>
        {!all && (
          <>
            <Form.Item name="connectionIds" label={t("orders.rule.stores")}>
              <Select mode="multiple" allowClear options={(connections.data?.items ?? []).map((x) => ({ value: x.id, label: x.name }))} />
            </Form.Item>
            <Form.Item name="platforms" label={t("orders.rule.platforms")}>
              <Select mode="multiple" allowClear options={PLATFORMS.map((p) => ({ value: p, label: t(`platform.${p}`) }))} />
            </Form.Item>
            <Form.Item name="paymentMethods" label={t("orders.rule.payment")} extra={t("orders.rule.paymentHelp")}>
              <Select mode="tags" allowClear tokenSeparators={[","]} />
            </Form.Item>
            <Space wrap>
              <Form.Item name="minTotal" label={t("orders.rule.minTotal")}>
                <InputNumber min={0} precision={0} prefix="Rp" style={{ width: 180 }} />
              </Form.Item>
              <Form.Item name="maxTotal" label={t("orders.rule.maxTotal")}>
                <InputNumber min={0} precision={0} prefix="Rp" style={{ width: 180 }} />
              </Form.Item>
              <Form.Item name="minUnits" label={t("orders.rule.minUnits")}>
                <InputNumber min={1} precision={0} style={{ width: 140 }} />
              </Form.Item>
            </Space>
            <Form.Item name="skuIds" label={t("orders.rule.skus")} extra={t("orders.rule.skusHelp")}>
              <Select
                mode="multiple"
                allowClear
                showSearch={{ filterOption: false, onSearch: setSkuSearch }}
                loading={skus.isFetching}
                options={(skus.data?.items ?? []).map((s) => ({ value: s.id, label: `${s.code} · ${s.name}` }))}
              />
            </Form.Item>
            <Form.Item name="carriers" label={t("orders.rule.carriers")}>
              <Select mode="tags" allowClear tokenSeparators={[","]} />
            </Form.Item>
            <Form.Item name="orderedBefore" label={t("orders.rule.orderedBefore")} extra={t("orders.rule.orderedBeforeHelp")}>
              <DatePicker showTime format="DD/MM/YYYY HH:mm" />
            </Form.Item>
          </>
        )}
        <Space wrap>
          <Form.Item name="untilAt" label={t("orders.rule.until")} extra={t("orders.rule.untilHelp")}>
            <DatePicker showTime format="DD/MM/YYYY HH:mm" />
          </Form.Item>
          <Form.Item name="active" label={t("orders.rule.active")} valuePropName="checked">
            <Switch />
          </Form.Item>
        </Space>
      </Form>
    </Modal>
  );
}

/** What happens to a buyer's cancellation request on an order already processed. */
function CancelSettings({ manage }: { manage: boolean }) {
  const { t } = useI18n();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const queryClient = useQueryClient();
  const q = useQuery({ queryKey: ["settings", "orders"], queryFn: () => unwrap(api.GET("/v1/settings/orders")) });
  const save = useMutation({
    mutationFn: (v: boolean) => unwrap(api.PUT("/v1/settings/orders", { body: { autoRejectPackedCancel: v } })),
    onSuccess: (x) => {
      queryClient.setQueryData(["settings", "orders"], x);
      void message.success(t("orders.cancelSettings.saved"));
    },
    onError: (err) => void message.error(errorText(err)),
  });
  return (
    <Card size="small" title={t("orders.cancelSettings.title")} style={{ marginBottom: 16 }}>
      <Space orientation="vertical" size={8}>
        <Typography.Text>{t("orders.cancelSettings.notPacked")}</Typography.Text>
        <Space align="start">
          <Switch
            checked={q.data?.autoRejectPackedCancel ?? true}
            loading={q.isPending || save.isPending}
            disabled={!manage}
            onChange={(v) => save.mutate(v)}
          />
          <Space orientation="vertical" size={0}>
            <Typography.Text strong>{t("orders.cancelSettings.autoReject")}</Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {t("orders.cancelSettings.autoRejectHelp")}
            </Typography.Text>
          </Space>
        </Space>
      </Space>
    </Card>
  );
}
